import React from 'react';

/**
 * ISO 3166-1 alpha-2 country picker as a type-ahead: the cashier types a country
 * name (Greek or English) or the code itself and picks from suggestions — the
 * fiscal provider rejects anything that isn't a valid alpha-2 code, and a bare
 * dropdown of codes was unreadable. The QUERY text is owned by the parent so the
 * on-screen TouchKeyboard can type into it like any other field.
 */
export const COUNTRIES: { code: string; el: string; en: string }[] = [
  { code: 'GR', el: 'Ελλάδα', en: 'Greece' },
  { code: 'CY', el: 'Κύπρος', en: 'Cyprus' },
  { code: 'AL', el: 'Αλβανία', en: 'Albania' },
  { code: 'BG', el: 'Βουλγαρία', en: 'Bulgaria' },
  { code: 'RO', el: 'Ρουμανία', en: 'Romania' },
  { code: 'MK', el: 'Βόρεια Μακεδονία', en: 'North Macedonia' },
  { code: 'RS', el: 'Σερβία', en: 'Serbia' },
  { code: 'TR', el: 'Τουρκία', en: 'Turkey' },
  { code: 'DE', el: 'Γερμανία', en: 'Germany' },
  { code: 'IT', el: 'Ιταλία', en: 'Italy' },
  { code: 'FR', el: 'Γαλλία', en: 'France' },
  { code: 'ES', el: 'Ισπανία', en: 'Spain' },
  { code: 'PT', el: 'Πορτογαλία', en: 'Portugal' },
  { code: 'NL', el: 'Ολλανδία', en: 'Netherlands' },
  { code: 'BE', el: 'Βέλγιο', en: 'Belgium' },
  { code: 'LU', el: 'Λουξεμβούργο', en: 'Luxembourg' },
  { code: 'AT', el: 'Αυστρία', en: 'Austria' },
  { code: 'CH', el: 'Ελβετία', en: 'Switzerland' },
  { code: 'GB', el: 'Ηνωμένο Βασίλειο', en: 'United Kingdom' },
  { code: 'IE', el: 'Ιρλανδία', en: 'Ireland' },
  { code: 'PL', el: 'Πολωνία', en: 'Poland' },
  { code: 'CZ', el: 'Τσεχία', en: 'Czechia' },
  { code: 'SK', el: 'Σλοβακία', en: 'Slovakia' },
  { code: 'SI', el: 'Σλοβενία', en: 'Slovenia' },
  { code: 'HR', el: 'Κροατία', en: 'Croatia' },
  { code: 'BA', el: 'Βοσνία-Ερζεγοβίνη', en: 'Bosnia and Herzegovina' },
  { code: 'ME', el: 'Μαυροβούνιο', en: 'Montenegro' },
  { code: 'XK', el: 'Κόσοβο', en: 'Kosovo' },
  { code: 'HU', el: 'Ουγγαρία', en: 'Hungary' },
  { code: 'DK', el: 'Δανία', en: 'Denmark' },
  { code: 'SE', el: 'Σουηδία', en: 'Sweden' },
  { code: 'NO', el: 'Νορβηγία', en: 'Norway' },
  { code: 'FI', el: 'Φινλανδία', en: 'Finland' },
  { code: 'EE', el: 'Εσθονία', en: 'Estonia' },
  { code: 'LV', el: 'Λετονία', en: 'Latvia' },
  { code: 'LT', el: 'Λιθουανία', en: 'Lithuania' },
  { code: 'MT', el: 'Μάλτα', en: 'Malta' },
  { code: 'UA', el: 'Ουκρανία', en: 'Ukraine' },
  { code: 'MD', el: 'Μολδαβία', en: 'Moldova' },
  { code: 'GE', el: 'Γεωργία', en: 'Georgia' },
  { code: 'AM', el: 'Αρμενία', en: 'Armenia' },
  { code: 'RU', el: 'Ρωσία', en: 'Russia' },
  { code: 'US', el: 'ΗΠΑ', en: 'United States' },
  { code: 'CA', el: 'Καναδάς', en: 'Canada' },
  { code: 'AU', el: 'Αυστραλία', en: 'Australia' },
  { code: 'NZ', el: 'Νέα Ζηλανδία', en: 'New Zealand' },
  { code: 'CN', el: 'Κίνα', en: 'China' },
  { code: 'JP', el: 'Ιαπωνία', en: 'Japan' },
  { code: 'KR', el: 'Νότια Κορέα', en: 'South Korea' },
  { code: 'IN', el: 'Ινδία', en: 'India' },
  { code: 'IL', el: 'Ισραήλ', en: 'Israel' },
  { code: 'AE', el: 'Ηνωμ. Αραβικά Εμιράτα', en: 'United Arab Emirates' },
  { code: 'SA', el: 'Σαουδική Αραβία', en: 'Saudi Arabia' },
  { code: 'QA', el: 'Κατάρ', en: 'Qatar' },
  { code: 'EG', el: 'Αίγυπτος', en: 'Egypt' },
  { code: 'ZA', el: 'Νότια Αφρική', en: 'South Africa' },
  { code: 'BR', el: 'Βραζιλία', en: 'Brazil' },
  { code: 'AR', el: 'Αργεντινή', en: 'Argentina' },
  { code: 'MX', el: 'Μεξικό', en: 'Mexico' },
];

const normalize = (s: string) =>
  s.normalize('NFD').replace(/[̀-ͯ]/g, '').toUpperCase().trim();

export const countryLabel = (code: string): string => {
  const c = COUNTRIES.find(x => x.code === (code || '').toUpperCase());
  return c ? `${c.code} — ${c.el}` : (code || '');
};

export const matchCountries = (query: string) => {
  const q = normalize(query);
  if (!q) return COUNTRIES.slice(0, 8);
  return COUNTRIES.filter(c =>
    c.code.startsWith(q) || normalize(c.el).includes(q) || normalize(c.en).includes(q)
  ).slice(0, 8);
};

interface CountryCodeInputProps {
  /** Selected ISO code (what actually gets submitted). */
  value: string;
  /** The typed text — parent-owned so the on-screen keyboard can write to it. */
  query: string;
  onQueryChange: (q: string) => void;
  onSelect: (code: string) => void;
  onFocus?: () => void;
  className?: string;
}

const CountryCodeInput: React.FC<CountryCodeInputProps> = ({ value, query, onQueryChange, onSelect, onFocus, className }) => {
  // Suggestions show while the query doesn't exactly represent the selection.
  const showSuggestions = query !== countryLabel(value);
  const suggestions = showSuggestions ? matchCountries(query) : [];

  return (
    <div className="relative">
      <input
        value={query}
        onChange={(e) => onQueryChange(e.target.value)}
        onFocus={onFocus}
        placeholder="π.χ. Ελλάδα / GR"
        className={className || 'mt-1 w-full h-9 rounded-md text-sm bg-surface-2 border border-line-strong text-t1 px-2 placeholder:text-t4'}
      />
      {showSuggestions && suggestions.length > 0 && (
        <div className="absolute z-20 left-0 right-0 mt-1 bg-surface-2 border border-line-strong rounded-lg shadow-xl max-h-48 overflow-y-auto">
          {suggestions.map(c => (
            <button
              key={c.code}
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => onSelect(c.code)}
              className="w-full px-3 py-2 text-left text-sm text-t1 hover:bg-surface-3 flex justify-between"
            >
              <span>{c.el}</span>
              <span className="text-t3 font-mono">{c.code}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default CountryCodeInput;
